import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Portal,
    TextField,
    IconButton,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    AddCard as AddCardIcon,
    DeleteOutline as DeleteIcon,
    SwapHoriz as ReplaceIcon,
    Search as SearchIcon,
    CreditCard as CreditCardIcon,
    CreditCardOff as CreditCardOffIcon,
    LockReset as LockResetIcon,
    Refresh as RefreshIcon,
    Close as ClearIcon,
    InfoOutlined as InfoIcon,
    ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import clientSavedCardsService from '../../../Services/clientSavedCards.service';
import s from './SavedCardsModal.module.scss';

const fieldSx = {
    height: '40px',
    padding: '0 12px',
    borderRadius: '10px',
    background: '#f5f6fd',
    width: '100%',
    border: '1px solid #e5e7f6',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
    '&:hover': { borderColor: '#c9cff5' },
    '&.Mui-focused, &:focus-within': {
        borderColor: '#304FFF',
        boxShadow: '0 0 0 3px rgba(48, 79, 255, 0.12)',
        background: '#fff',
    },
};

const FILTER_ALL = 'all';
const FILTER_WITH = 'with';
const FILTER_WITHOUT = 'without';
const FILTER_EXPIRED = 'expired';
const FILTER_MISMATCH = 'mismatch';

/** Card expires at the end of its expiry month; supports 2- and 4-digit years. */
function isCardExpired(row) {
    if (!row?.savedCardId || !row.expiryMonth || !row.expiryYear) return false;
    const month = Number(row.expiryMonth);
    let year = Number(row.expiryYear);
    if (year < 100) year += 2000;
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth() + 1;
    return year < curYear || (year === curYear && month < curMonth);
}

const isProviderMismatch = (row, terminalProviderCode) =>
    row?.savedCardId != null
    && row?.providerCode != null
    && terminalProviderCode != null
    && Number(row.providerCode) !== Number(terminalProviderCode);

const EMPTY_FORM = { cardNumber: '', month: '', year: '', cvv: '', cardHolderId: '' };

const Field = ({ label, required = false, className = '', children }) => (
    <div className={`${s.field}${className ? ` ${className}` : ''}`}>
        <span className={s.fieldLabel}>
            {label}
            {required && <span className={s.required}>*</span>}
        </span>
        {children}
    </div>
);

/**
 * "Manage saved cards" modal opened from the Collection / MASAV context.
 *
 * Two modes, driven by <code>leadingBusiness</code>:
 *   - leadingBusiness = true → table of every client under the accountant
 *     with their saved-card status + per-row Add / Replace / Delete actions.
 *   - leadingBusiness = false → single-client mode for <code>client</code>.
 *
 * Cards are stored as provider tokens server-side; the accountant typing card
 * details here is the same PCI flow as the existing Charge panel.
 *
 * <code>supportsRawPan</code> — false when the accountant's terminal provider refuses
 * raw card numbers (Grow). Add / Replace / Update-CVV are hidden then: Grow tokens are
 * created when the client pays a payment link with save-card consent. Charging and
 * deleting existing tokens still work.
 *
 * <code>terminalProviderCode</code> — the accountant's current clearing provider
 * (0=Tranzila, 1=Grow). Tokens are not portable across providers, so cards saved
 * under a different provider get a warning chip (charging them would fail with 400).
 */
export default function SavedCardsModal({
    open,
    onClose,
    client,
    leadingBusiness = false,
    direction = 'rtl',
    onChanged,
    supportsRawPan = true,
    terminalProviderCode = null,
}) {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [cardFilter, setCardFilter] = useState(FILTER_ALL);

    // Inline card-entry form state. Holds the targeted row while the user
    // fills card details. {row, replacing} when active.
    const [formTarget, setFormTarget] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);

    const refresh = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        setLoadError('');
        try {
            const data = await clientSavedCardsService.list({
                leadingBusiness,
                clientCaseId: leadingBusiness ? undefined : client?.caseId,
            });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setLoadError(e?.message || t('Error'));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [open, leadingBusiness, client?.caseId, t]);

    useEffect(() => { refresh(); }, [refresh]);

    const providerMismatch = (row) => isProviderMismatch(row, terminalProviderCode);

    const stats = useMemo(() => {
        let withCard = 0;
        let expired = 0;
        let mismatch = 0;
        rows.forEach((r) => {
            if (!r.savedCardId) return;
            withCard += 1;
            if (isCardExpired(r)) expired += 1;
            if (isProviderMismatch(r, terminalProviderCode)) mismatch += 1;
        });
        return { total: rows.length, withCard, withoutCard: rows.length - withCard, expired, mismatch };
    }, [rows, terminalProviderCode]);

    const filteredRows = useMemo(() => {
        if (!leadingBusiness) return rows;
        let list = rows;
        if (cardFilter === FILTER_WITH) list = list.filter((r) => !!r.savedCardId);
        else if (cardFilter === FILTER_WITHOUT) list = list.filter((r) => !r.savedCardId);
        else if (cardFilter === FILTER_EXPIRED) list = list.filter(isCardExpired);
        else if (cardFilter === FILTER_MISMATCH) list = list.filter((r) => isProviderMismatch(r, terminalProviderCode));
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((r) => {
            const name = (r.clientName || '').toLowerCase();
            const caseId = (r.caseId || '').toLowerCase();
            const last4 = (r.last4 || '').toLowerCase();
            return name.includes(q) || caseId.includes(q) || last4.includes(q);
        });
    }, [rows, search, leadingBusiness, cardFilter, terminalProviderCode]);

    const beginAdd = (row, replacing = false) => {
        setFormTarget({ row, replacing });
        setForm(EMPTY_FORM);
    };
    const cancelForm = useCallback(() => {
        setFormTarget(null);
        setForm(EMPTY_FORM);
    }, []);

    const canSubmitForm = useMemo(() => {
        const digits = form.cardNumber.replace(/\s/g, '');
        const m = Number(form.month);
        const y = Number(form.year);
        return digits.length >= 12 && m >= 1 && m <= 12 && y > 0 && form.cvv.length >= 3 && !submitting;
    }, [form, submitting]);

    const submitForm = async () => {
        if (!formTarget?.row?.caseId || !canSubmitForm) return;
        setSubmitting(true);
        try {
            await clientSavedCardsService.save({
                clientCaseId: formTarget.row.caseId,
                cardNumber: form.cardNumber.replace(/\s/g, ''),
                expiryMonth: Number(form.month),
                expiryYear: Number(form.year),
                cvv: form.cvv,
                cardHolderId: form.cardHolderId?.trim() || null,
            });
            toast.success(t('SavedCards.saveSuccess'));
            cancelForm();
            await refresh();
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || t('SavedCards.saveFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateCvv = async (row) => {
        if (!row?.savedCardId) return;
        const { value: cvv, isConfirmed } = await Swal.fire({
            title: t('SavedCards.updateCvvTitle', { defaultValue: 'Update CVV' }),
            text: t('SavedCards.updateCvvHint', {
                defaultValue: 'Enter the 3 or 4 digit CVV from the back of the card. Tranzila verifies CVV on every charge for some issuers.',
            }),
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off',
                inputmode: 'numeric',
                maxlength: 4,
                pattern: '\\d{3,4}',
            },
            inputValidator: (val) => {
                if (!val) return t('SavedCards.cvvRequired', { defaultValue: 'CVV is required.' });
                if (!/^\d{3,4}$/.test(val)) return t('SavedCards.cvvInvalid', { defaultValue: 'CVV must be 3 or 4 digits.' });
                return null;
            },
            showCancelButton: true,
            confirmButtonText: t('Save'),
            cancelButtonText: t('Cancel'),
            confirmButtonColor: '#304FFF',
        });
        if (!isConfirmed || !cvv) return;
        try {
            await clientSavedCardsService.updateCvv(row.savedCardId, cvv);
            toast.success(t('SavedCards.updateCvvSuccess', { defaultValue: 'CVV updated.' }));
            await refresh();
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || t('SavedCards.updateCvvFailed', { defaultValue: 'Failed to update CVV.' }));
        }
    };

    const handleDelete = async (row) => {
        if (!row?.savedCardId) return;
        const confirmed = await Swal.fire({
            title: t('SavedCards.deleteTitle'),
            text: t('SavedCards.deleteConfirm', { name: row.clientName || row.caseId }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Delete'),
            cancelButtonText: t('Cancel'),
        });
        if (!confirmed.isConfirmed) return;
        try {
            await clientSavedCardsService.remove(row.savedCardId);
            toast.success(t('SavedCards.deleteSuccess'));
            await refresh();
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || t('SavedCards.deleteFailed'));
        }
    };

    const handleClose = useCallback(() => {
        if (!submitting) onClose?.();
    }, [submitting, onClose]);

    // Escape: cancel the open card form first, otherwise close the modal.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || Swal.isVisible() || submitting) return;
            if (formTarget) cancelForm();
            else handleClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, submitting, formTarget, handleClose, cancelForm]);

    if (!open) return null;

    const renderRowActions = (row) => {
        if (row.savedCardId) {
            return (
                <div className={s.rowActions}>
                    {supportsRawPan && (
                        <>
                            <Tooltip title={t('SavedCards.updateCvvHint')}>
                                <span>
                                    <button
                                        type="button"
                                        className={`${s.ghostBtn} ${s.smallBtn}`}
                                        onClick={() => handleUpdateCvv(row)}
                                        disabled={submitting}
                                    >
                                        <LockResetIcon fontSize="small" />
                                        {t('SavedCards.updateCvv', { defaultValue: 'Update CVV' })}
                                    </button>
                                </span>
                            </Tooltip>
                            <button
                                type="button"
                                className={`${s.secondaryBtn} ${s.smallBtn}`}
                                onClick={() => beginAdd(row, true)}
                                disabled={submitting}
                            >
                                <ReplaceIcon fontSize="small" />
                                {t('SavedCards.replace')}
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        className={`${s.dangerBtn} ${s.smallBtn}`}
                        onClick={() => handleDelete(row)}
                        disabled={submitting}
                    >
                        <DeleteIcon fontSize="small" />
                        {t('SavedCards.delete')}
                    </button>
                </div>
            );
        }
        if (!supportsRawPan) return null;
        return (
            <div className={s.rowActions}>
                <button
                    type="button"
                    className={`${s.primaryBtn} ${s.smallBtn}`}
                    onClick={() => beginAdd(row, false)}
                    disabled={submitting}
                >
                    <AddCardIcon fontSize="small" />
                    {t('SavedCards.add')}
                </button>
            </div>
        );
    };

    const renderCard = (row) => {
        if (!row.savedCardId) {
            return (
                <div className={s.cardInfo}>
                    <span className={`${s.cardBadge} ${s.cardBadgeMuted}`}>
                        <CreditCardOffIcon fontSize="small" />
                    </span>
                    <span className={`${s.chip} ${s.chipMuted}`}>{t('SavedCards.noCard')}</span>
                </div>
            );
        }
        const mm = row.expiryMonth?.toString().padStart(2, '0');
        const yy = row.expiryYear ? String(row.expiryYear).slice(-2) : '';
        const isGrowToken = Number(row.providerCode) === 1;
        const expired = isCardExpired(row);
        return (
            <div className={s.cardInfo}>
                <span className={`${s.cardBadge}${expired ? ` ${s.cardBadgeExpired}` : ''}`}>
                    <CreditCardIcon fontSize="small" />
                </span>
                <div className={s.cardText}>
                    <span className={`${s.cardNumber} ${s.num}`} dir="ltr">•••• {row.last4}</span>
                    <div className={s.chipRow}>
                        {mm && yy ? (
                            <span className={`${s.chip} ${expired ? s.chipError : s.chipMuted}`}>
                                <span className={s.num} dir="ltr">{`${mm}/${yy}`}</span>
                            </span>
                        ) : null}
                        {expired && (
                            <span className={`${s.chip} ${s.chipError}`}>{t('Expired')}</span>
                        )}
                        {row.cardTypeName ? (
                            <span className={s.chip}>{row.cardTypeName}</span>
                        ) : null}
                        {row.providerCode != null && (
                            <span className={`${s.chip}${isGrowToken ? ` ${s.chipSuccess}` : ''}`}>
                                {isGrowToken ? 'Grow' : 'Tranzila'}
                            </span>
                        )}
                        {providerMismatch(row) && (
                            <Tooltip title={t('SavedCards.providerMismatchHint')}>
                                <span className={`${s.chip} ${s.chipWarning}`}>
                                    {t('SavedCards.providerMismatch')}
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderFormFor = (row) => {
        const inputProps = (extra) => ({
            disableUnderline: true,
            sx: fieldSx,
            ...extra,
        });
        return (
            <div
                className={s.formPanel}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSubmitForm) {
                        e.preventDefault();
                        submitForm();
                    }
                }}
            >
                <p className={s.formTitle}>
                    {formTarget.replacing ? <ReplaceIcon fontSize="small" /> : <AddCardIcon fontSize="small" />}
                    {formTarget.replacing ? t('SavedCards.replaceFor', { name: row.clientName || row.caseId })
                        : t('SavedCards.addFor', { name: row.clientName || row.caseId })}
                </p>
                <div className={s.formGrid}>
                    <Field label={t('SavedCards.cardNumber')} required className={s.fieldCardNumber}>
                        <TextField
                            variant="standard"
                            autoFocus
                            value={form.cardNumber}
                            onChange={(e) => setForm((p) => ({ ...p, cardNumber: e.target.value.replace(/[^\d\s]/g, '').slice(0, 19) }))}
                            fullWidth
                            InputProps={inputProps()}
                            inputProps={{ inputMode: 'numeric', dir: 'ltr', placeholder: '•••• •••• •••• ••••' }}
                            autoComplete="off"
                        />
                    </Field>
                    <Field label={t('SavedCards.expiryMonth')} required>
                        <TextField
                            variant="standard"
                            value={form.month}
                            onChange={(e) => setForm((p) => ({ ...p, month: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                            fullWidth
                            InputProps={inputProps()}
                            inputProps={{ inputMode: 'numeric', maxLength: 2, dir: 'ltr' }}
                            placeholder="MM"
                        />
                    </Field>
                    <Field label={t('SavedCards.expiryYear')} required>
                        <TextField
                            variant="standard"
                            value={form.year}
                            onChange={(e) => setForm((p) => ({ ...p, year: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                            fullWidth
                            InputProps={inputProps()}
                            inputProps={{ inputMode: 'numeric', maxLength: 4, dir: 'ltr' }}
                            placeholder="YYYY"
                        />
                    </Field>
                    <Field label={t('SavedCards.cvv')} required>
                        <TextField
                            variant="standard"
                            value={form.cvv}
                            onChange={(e) => setForm((p) => ({ ...p, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                            fullWidth
                            InputProps={inputProps()}
                            inputProps={{ inputMode: 'numeric', maxLength: 4, dir: 'ltr' }}
                            autoComplete="off"
                        />
                    </Field>
                    <Field label={t('SavedCards.cardHolderId')} className={s.fieldWide}>
                        <TextField
                            variant="standard"
                            value={form.cardHolderId}
                            onChange={(e) => setForm((p) => ({ ...p, cardHolderId: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                            fullWidth
                            InputProps={inputProps()}
                            inputProps={{ inputMode: 'numeric', maxLength: 9, dir: 'ltr' }}
                        />
                    </Field>
                </div>
                <div className={s.formActions}>
                    <button type="button" className={s.ghostBtn} onClick={cancelForm} disabled={submitting}>
                        {t('Cancel')}
                    </button>
                    <button type="button" className={s.primaryBtn} onClick={submitForm} disabled={!canSubmitForm}>
                        {submitting ? <CircularProgress size={16} color="inherit" /> : <AddCardIcon fontSize="small" />}
                        {t('SavedCards.saveCard')}
                    </button>
                </div>
            </div>
        );
    };

    const renderStats = () => {
        const cards = [
            { id: FILTER_ALL, label: t('TotalClients'), value: stats.total },
            { id: FILTER_WITH, label: t('SavedCards.withCard', { defaultValue: 'With saved card' }), value: stats.withCard, tone: s.statCardSuccess },
            { id: FILTER_WITHOUT, label: t('SavedCards.noCard'), value: stats.withoutCard },
            { id: FILTER_EXPIRED, label: t('Expired'), value: stats.expired, tone: stats.expired > 0 ? s.statCardError : '' },
        ];
        if (stats.mismatch > 0) {
            cards.push({ id: FILTER_MISMATCH, label: t('SavedCards.providerMismatch'), value: stats.mismatch, tone: s.statCardWarning });
        }
        return (
            <div className={s.stats}>
                {cards.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        className={`${s.statCard}${c.tone ? ` ${c.tone}` : ''}${cardFilter === c.id ? ` ${s.statCardActive}` : ''}`}
                        aria-pressed={cardFilter === c.id}
                        onClick={() => setCardFilter((prev) => (prev === c.id && c.id !== FILTER_ALL ? FILTER_ALL : c.id))}
                    >
                        <span className={s.statLabel}>{c.label}</span>
                        <span className={s.statValue}>{c.value}</span>
                    </button>
                ))}
            </div>
        );
    };

    const renderTable = () => (
        <div className={s.tableWrap} dir={direction}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('SavedCards.client')}</TableCell>
                        <TableCell>{t('SavedCards.card')}</TableCell>
                        <TableCell className={s.actionsCell}>{t('SavedCards.actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredRows.map((row) => {
                        const editing = formTarget?.row?.caseId === row.caseId;
                        return (
                            <React.Fragment key={row.caseId}>
                                <TableRow hover className={editing ? s.rowEditing : undefined}>
                                    <TableCell>
                                        <div className={s.clientCell}>
                                            <span className={s.clientName}>{row.clientName || row.caseId}</span>
                                            <span className={s.clientCase}>{row.caseId}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>{renderCard(row)}</TableCell>
                                    <TableCell className={s.actionsCell}>
                                        {renderRowActions(row)}
                                    </TableCell>
                                </TableRow>
                                {editing && (
                                    <TableRow>
                                        <TableCell colSpan={3} className={s.formCell}>
                                            {renderFormFor(row)}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div className={s.loading}>
                    <CircularProgress className={s.spinner} />
                </div>
            );
        }
        if (rows.length === 0) {
            if (loadError) return null;
            return (
                <div className={s.emptyState}>
                    <CreditCardOffIcon className={s.emptyIcon} />
                    {t('SavedCards.empty')}
                </div>
            );
        }
        if (filteredRows.length === 0) {
            return (
                <div className={s.emptyState}>
                    <SearchIcon className={s.emptyIcon} />
                    {t('NoSearchResults')}
                    {(search || cardFilter !== FILTER_ALL) && (
                        <button
                            type="button"
                            className={`${s.ghostBtn} ${s.smallBtn}`}
                            onClick={() => { setSearch(''); setCardFilter(FILTER_ALL); }}
                        >
                            <ClearIcon fontSize="small" />
                            {t('Clear')}
                        </button>
                    )}
                </div>
            );
        }
        return renderTable();
    };

    const hasFilter = !!search.trim() || cardFilter !== FILTER_ALL;

    return (
        <Portal>
            <div className={`${s.overlay} ${isRtl ? s.rtl : s.ltr}`} onClick={handleClose}>
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${s.modal}${leadingBusiness ? ` ${s.modalWide}` : ''}`} role="dialog" aria-modal="true">
                        <div className={s.body}>
                            <div className={s.header}>
                                <Tooltip title={t('Close')}>
                                    <span>
                                        <IconButton
                                            onClick={handleClose}
                                            className={s.closeIcon}
                                            aria-label={t('Close')}
                                            disabled={submitting}
                                        >
                                            <CancelIcon className={s.closeIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div className={s.headerTitle}>
                                    <span className={s.headerTitleRow}>
                                        <CreditCardIcon />
                                        {leadingBusiness ? t('SavedCards.titleLeading') : t('SavedCards.titleSingle')}
                                    </span>
                                    <span className={s.headerSubtitle}>{t('SavedCards.hint')}</span>
                                </div>
                                <div className={s.headerSpacer} />
                            </div>

                            <div className={s.scroll}>
                                {!supportsRawPan && (
                                    <div className={s.notice}>
                                        <InfoIcon fontSize="small" className={s.noticeIcon} />
                                        <span className={s.noticeText}>{t('SavedCards.hostedOnlyHint')}</span>
                                    </div>
                                )}

                                {loadError && (
                                    <div className={`${s.notice} ${s.noticeError}`} role="alert">
                                        <ErrorIcon fontSize="small" className={s.noticeIcon} />
                                        <span className={s.noticeText}>{loadError}</span>
                                        <button
                                            type="button"
                                            className={`${s.dangerBtn} ${s.smallBtn}`}
                                            onClick={refresh}
                                            disabled={loading}
                                        >
                                            <RefreshIcon fontSize="small" />
                                            {t('TryAgain')}
                                        </button>
                                    </div>
                                )}

                                {leadingBusiness && !loading && rows.length > 0 && (
                                    <>
                                        {renderStats()}
                                        <div className={s.toolbar}>
                                            <TextField
                                                variant="standard"
                                                className={s.searchField}
                                                placeholder={t('SavedCards.searchPlaceholder')}
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                inputProps={{ dir: direction }}
                                                InputProps={{
                                                    disableUnderline: true,
                                                    sx: fieldSx,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" sx={{ color: '#304FFF' }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: search ? (
                                                        <InputAdornment position="end">
                                                            <IconButton size="small" onClick={() => setSearch('')} aria-label={t('Clear')}>
                                                                <ClearIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                            />
                                            {hasFilter && (
                                                <span className={s.resultCount}>
                                                    {`${filteredRows.length} / ${rows.length}`}
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}

                                {renderContent()}

                                {!leadingBusiness && filteredRows.length > 0 && (
                                    <div className={s.singleNote}>
                                        <InfoIcon fontSize="small" />
                                        <span>{t('SavedCards.singleNote')}</span>
                                    </div>
                                )}
                            </div>

                            <div className={s.footer}>
                                <button
                                    type="button"
                                    className={s.ghostBtn}
                                    onClick={refresh}
                                    disabled={loading || submitting}
                                >
                                    {loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
                                    {t('Refresh')}
                                </button>
                                <span className={s.footerSpacer} />
                                <button
                                    type="button"
                                    className={s.secondaryBtn}
                                    onClick={handleClose}
                                    disabled={submitting}
                                >
                                    {t('Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
}
